import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ============================================================
 * SHARED MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 0 2em;
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ============================================================
   * HEADER — padrão unificado (espelha XML Scan)
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "CERTIFICADOS · GESTÃO DIGITAL";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow || tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * KPI CARDS
   * ============================================================ */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.success .card-content .text p:first-child { color: ${tokens.brand}; }
    &.warning .card-content .text p:first-child { color: ${tokens.warn}; }
    &.danger  .card-content .text p:first-child { color: ${tokens.danger}; }

    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 4px;
      ${({ theme }) => cardBase(theme)};
      padding: 3px;
    }

    .btn-new {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.brand};
      color: white;
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      font-size: 0.78em;
      font-weight: 500;
      letter-spacing: -0.005em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      svg { font-size: 0.95em; }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 360px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.9em;
        flex-shrink: 0;
      }

      input {
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        flex: 1;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }

    .filter-pill {
      ${mono};
      padding: 6px 12px;
      background: transparent;
      border: none;
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-size: 0.66em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover { opacity: 0.9; }

      &.active {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        opacity: 1;
      }
    }
  }

  /* ============================================================
   * EXPIRED BANNER
   * ============================================================ */
  .expired-banner {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    margin-bottom: 14px;
    background: ${tokens.dangerSoft};
    border: 1px solid rgba(239, 68, 68, 0.22);
    border-radius: ${tokens.radiusLg};
    flex-shrink: 0;
    animation: ${fadeInOnly} ${tokens.durBase} ${tokens.easeOut};

    .banner-icon {
      width: 36px;
      height: 36px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(239, 68, 68, 0.12);
      border: 1px solid rgba(239, 68, 68, 0.22);
      border-radius: ${tokens.radiusMd};
      color: ${tokens.danger};
      flex-shrink: 0;

      svg { font-size: 1em; }
    }

    .banner-text {
      flex: 1;
      min-width: 0;

      h4 {
        margin: 0;
        font-size: 0.85em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.005em;
      }

      p {
        margin: 2px 0 0 0;
        font-size: 0.72em;
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        letter-spacing: -0.005em;
      }
    }

    .banner-action {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: ${tokens.danger};
      color: white;
      border: 1px solid ${tokens.danger};
      border-radius: ${tokens.radiusMd};
      font-size: 0.74em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;
      flex-shrink: 0;

      &:hover { transform: translateY(-1px); filter: brightness(1.05); }

      svg { font-size: 0.95em; }
    }
  }

  /* ============================================================
   * MAIN CONTENT (scroll wrapper)
   * ============================================================ */
  .main-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
  }

  /* ============================================================
   * CERTIFICATES GRID — 4-col compact
   * ============================================================ */
  .cert-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    padding-bottom: 24px;

    @media (max-width: 1400px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 720px)  { grid-template-columns: 1fr; }
  }

  .cert-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 160px;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 2px;
      border-radius: 0 2px 2px 0;
      opacity: 0.55;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};
    }

    &.ok::before     { background: ${tokens.brand}; }
    &.warn::before   { background: ${tokens.warn}; }
    &.danger::before { background: ${tokens.danger}; }

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
      &::before { opacity: 1; }

      .cert-actions { opacity: 1; }
    }

    .cert-top {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;

      .cert-eyebrow {
        ${eyebrow};
        font-size: 0.55em;
        opacity: 0.45;
        letter-spacing: 0.06em;
      }

      h5 {
        margin: 0;
        font-size: 0.92em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.01em;
        line-height: 1.25;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        text-transform: capitalize;
      }

      .cert-location {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-wrap: wrap;
        margin-top: 2px;
      }

      .location-tag {
        display: inline-flex;
        align-items: center;
        padding: 2px 10px;
        border-radius: ${tokens.radiusPill};
        font-family: ${tokens.fontSans};
        font-size: 0.66em;
        font-weight: 700;
        letter-spacing: 0.01em;
        line-height: 1.5;
        color: #fff;
        text-transform: capitalize;
        white-space: nowrap;
        border: none;

        &.neutral {
          ${mono};
          font-size: 0.62em;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          background: ${({ theme }) => surface(theme, 1)};
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          border: 1px solid ${({ theme }) => hairline(theme)};
        }
      }
    }

    .cert-actions {
      position: absolute;
      top: 10px;
      right: 10px;
      opacity: 0;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};

      .btn-delete {
        width: 26px;
        height: 26px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 0.85em; }

        &:hover {
          background: ${tokens.dangerSoft};
          border-color: rgba(239, 68, 68, 0.3);
          color: ${tokens.danger};
          opacity: 1;
        }
      }
    }

    .cert-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      margin-top: auto;

      .cert-validity {
        ${mono};
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.66em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        letter-spacing: 0.01em;
        min-width: 0;

        svg { font-size: 0.95em; opacity: 0.7; flex-shrink: 0; }

        span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .status-pill {
        ${mono};
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 8px;
        border-radius: ${tokens.radiusPill};
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        border: 1px solid transparent;
        white-space: nowrap;
        flex-shrink: 0;

        svg { font-size: 0.95em; }

        &.ok {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        }
        &.warn {
          background: ${tokens.warnSoft};
          color: ${tokens.warn};
          border-color: rgba(245, 158, 11, 0.22);
        }
        &.danger {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.22);
        }
      }
    }

    .cert-renew-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 7px 12px;
      background: transparent;
      border: 1px solid;
      border-radius: ${tokens.radiusMd};
      font-family: ${tokens.fontSans};
      font-size: 0.72em;
      font-weight: 500;
      letter-spacing: -0.005em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      width: 100%;

      svg { font-size: 0.95em; }

      &.warning {
        background: ${tokens.warnSoft};
        border-color: rgba(245, 158, 11, 0.3);
        color: ${tokens.warn};

        &:hover {
          background: rgba(245, 158, 11, 0.18);
          transform: translateY(-1px);
        }
      }

      &.danger {
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.3);
        color: ${tokens.danger};

        &:hover {
          background: rgba(239, 68, 68, 0.18);
          transform: translateY(-1px);
        }
      }
    }
  }

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    .empty-icon {
      width: 64px;
      height: 64px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;

      svg { font-size: 1.6em; }
    }

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }

    .empty-cta {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 9px 18px;
      background: ${tokens.brand};
      color: white;
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      font-size: 0.8em;
      font-weight: 500;
      cursor: pointer;
      margin-top: 8px;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      svg { font-size: 1em; }
    }
  }
`;
